import React, { useState } from 'react';
import {
  CheckIcon,
  StarIcon,
  ClockIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  CloudIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const PlanSelection = ({ data, onUpdate, plans, plansLoading, errors, setErrors, countryCode, currency }) => {
  const [selectedPlan, setSelectedPlan] = useState(data.planId || '');
  const [billingCycle, setBillingCycle] = useState(data.billingCycle || 'yearly');

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    const selectedPlanObject = plans.find(plan => plan._id === planId);
    onUpdate({
      planId,
      billingCycle,
      selectedPlan: selectedPlanObject
    });
    if (errors.planId) {
      setErrors(prev => ({ ...prev, planId: '' }));
    }
  };

  const handleBillingCycleChange = (cycle) => {
    setBillingCycle(cycle);
    const selectedPlanObject = plans.find(plan => plan._id === selectedPlan);
    onUpdate({
      planId: selectedPlan,
      billingCycle: cycle,
      selectedPlan: selectedPlanObject
    });
  };

  const getFeatureIcon = (feature) => {
    switch (feature) {
      case 'employees':
        return <UserGroupIcon className="h-5 w-5" />;
      case 'branches':
        return <BuildingOfficeIcon className="h-5 w-5" />;
      case 'storage':
        return <CloudIcon className="h-5 w-5" />;
      default:
        return <CheckIcon className="h-5 w-5" />;
    }
  };

  const formatPrice = (price, cycle, planCurrency) => {
    const currencySymbol = getCurrencySymbol(planCurrency || currency || 'USD');

    if (cycle === 'yearly') {
      const monthlyEquivalent = (price / 12).toFixed(0);
      return `${currencySymbol}${monthlyEquivalent}`;
    }
    return `${currencySymbol}${price}`;
  };

  const formatYearlyPrice = (price, planCurrency) => {
    const currencySymbol = getCurrencySymbol(planCurrency || currency || 'USD');
    return `${currencySymbol}${price?.toFixed(2)}`;
  };

  const getCurrencySymbol = (curr) => {
    const symbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'CAD': 'C$',
      'AUD': 'A$',
      'INR': '₹',
      'SGD': 'S$',
      'AED': 'د.إ',
      'BRL': 'R$',
      'MXN': '$',
      'ZAR': 'R',
      'NGN': '₦',
      'KES': 'KSh',
      'EGP': '£'
    };
    return symbols[curr] || '$';
  };

  if (plansLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading plans...</p>
      </div>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <BuildingOfficeIcon className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Plans Available</h3>
        <p className="text-gray-600 mb-4">
          We're currently not offering plans for your selected country ({countryCode}).
          Please contact our support team for custom pricing.
        </p>
        <p className="text-sm text-gray-500">
          Selected Country: {countryCode} | Currency: {currency}
        </p>
        <p className="text-xs text-red-500 mt-2">
          Debug: {plansLoading ? 'Loading...' : `${plans?.length || 0} plans found`}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Choose Your Plan
        </h2>
        <p className="text-lg text-gray-600 mb-4">
          Select the perfect plan for your business needs
        </p>
      </div>

      {/* Billing Cycle Toggle */}
      <div className="flex justify-center mb-8">
        <div className="bg-gray-100 p-1 rounded-xl">
          <button
            onClick={() => handleBillingCycleChange('monthly')}
            className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
              billingCycle === 'monthly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => handleBillingCycleChange('yearly')}
            className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
              billingCycle === 'yearly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Yearly
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        {Array.isArray(plans) && plans.map((plan) => {
          const isSelected = selectedPlan === plan._id;
          const price = plan.price[billingCycle];
          const displayPrice = formatPrice(price, billingCycle, plan.currency);
          const yearlyPrice = formatYearlyPrice(plan.price.yearly, plan.currency);

          return (
            <div
              key={plan._id}
              onClick={() => handlePlanSelect(plan._id)}
              className={`relative bg-white rounded-2xl border-2 p-8 cursor-pointer transition-all duration-300 hover:shadow-lg transform hover:scale-105 ${
                isSelected
                  ? 'border-blue-500 shadow-lg scale-105'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {plan.isPopular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-orange-400 to-red-500 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center">
                    <StarIconSolid className="h-4 w-4 mr-1" />
                    Most Popular
                  </div>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {plan.name}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {plan.description}
                </p>

                <div className="mb-4">
                  {plan.originalPrice && plan.discountPercentage > 0 && (
                    <div className="flex items-center justify-center mb-2">
                      <span className="text-lg text-gray-500 line-through mr-2">
                        {getCurrencySymbol(plan.currency || currency || 'USD')}{plan.originalPrice}
                      </span>
                      <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                        {plan.discountPercentage}% OFF
                      </span>
                    </div>
                  )}
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-gray-900">
                      {displayPrice}
                    </span>
                    <span className="text-gray-600 ml-2">
                      /{billingCycle === 'yearly' ? 'mo' : 'month'}
                    </span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <p className="text-sm text-green-600 font-medium mt-1">
                      Billed {yearlyPrice} annually
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-center text-sm text-gray-600 mb-4">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  {plan.trialDays} days free trial
                </div>
              </div>

              {/* Features */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center text-sm">
                  <UserGroupIcon className="h-5 w-5 text-blue-600 mr-3" />
                  <span>Up to {plan.features.maxEmployees} employees</span>
                </div>
                <div className="flex items-center text-sm">
                  <BuildingOfficeIcon className="h-5 w-5 text-blue-600 mr-3" />
                  <span>Up to {plan.features.maxBranches} branches</span>
                </div>
                <div className="flex items-center text-sm">
                  <CloudIcon className="h-5 w-5 text-blue-600 mr-3" />
                  <span>{plan.features.storage}GB storage</span>
                </div>

                {plan.features.modules.map((module) => (
                  <div key={module} className="flex items-center text-sm">
                    <CheckIcon className="h-5 w-5 text-green-600 mr-3" />
                    <span className="capitalize">{module}</span>
                  </div>
                ))}
              </div>

              {/* Selection Indicator */}
              {isSelected && (
                <div className="absolute top-4 right-4">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                    <CheckIcon className="h-4 w-4 text-white" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {errors.planId && (
        <p className="text-red-600 text-center font-medium">{errors.planId}</p>
      )}

      {/* Plan Comparison */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          Why Choose Our Plans?
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <UserGroupIcon className="h-6 w-6 text-blue-600" />
            </div>
            <h5 className="font-medium text-gray-900 mb-2">Scalable</h5>
            <p className="text-sm text-gray-600">
              Grow your business with plans that scale with you
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <CheckIcon className="h-6 w-6 text-green-600" />
            </div>
            <h5 className="font-medium text-gray-900 mb-2">Feature-Rich</h5>
            <p className="text-sm text-gray-600">
              All essential HR features in one comprehensive platform
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <ClockIcon className="h-6 w-6 text-purple-600" />
            </div>
            <h5 className="font-medium text-gray-900 mb-2">Free Trial</h5>
            <p className="text-sm text-gray-600">
              Try before you buy with our generous trial period
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanSelection;
